import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const defaultData = [
  { avgEntry: "0-50k", "15min": 0, "30min": 0, "1h": 0, "2h": 0, "4h": 0, "6h": 0, "8h": 0, "12h": 5, "24h": 0, "48h": 0, "5d": 5, "1w": 0 },
  { avgEntry: "50-100k", "15min": 0, "30min": 0, "1h": 0, "2h": 0, "4h": 0, "6h": 5, "8h": 0, "12h": 5, "24h": 5, "48h": 6, "5d": 7, "1w": 8 },
  { avgEntry: "100-150k", "15min": 0, "30min": 0, "1h": 0, "2h": 0, "4h": 0, "6h": 4, "8h": 0, "12h": 5, "24h": 0, "48h": 7, "5d": 8, "1w": 9 },
  { avgEntry: "150-200k", "15min": 0, "30min": 0, "1h": 3, "2h": 3, "4h": 4, "6h": 4, "8h": 0, "12h": 5, "24h": 5, "48h": 7, "5d": 8, "1w": 9 },
  { avgEntry: "200-250k", "15min": 0, "30min": 0, "1h": 3, "2h": 4, "4h": 5, "6h": 5, "8h": 5, "12h": 6, "24h": 0, "48h": 1, "5d": 9, "1w": 10 },
  { avgEntry: "250-300k", "15min": 0, "30min": 0, "1h": 4, "2h": 4, "4h": 5, "6h": 5, "8h": 0, "12h": 6, "24h": 5, "48h": 8, "5d": 5, "1w": 0 },
  { avgEntry: "300-400k", "15min": 0, "30min": 2, "1h": 4, "2h": 5, "4h": 5, "6h": 6, "8h": 5, "12h": 7, "24h": 0, "48h": 0, "5d": 0, "1w": 0 },
  { avgEntry: "400-500k", "15min": 0, "30min": 5, "1h": 5, "2h": 5, "4h": 6, "6h": 6, "8h": 7, "12h": 7, "24h": 5, "48h": 0, "5d": 0, "1w": 0 },
  { avgEntry: "500-600k", "15min": 0, "30min": 3, "1h": 5, "2h": 6, "4h": 6, "6h": 7, "8h": 7, "12h": 8, "24h": 0, "48h": 0, "5d": 0, "1w": 0 },
  { avgEntry: "600-700k", "15min": 5, "30min": 5, "1h": 6, "2h": 6, "4h": 7, "6h": 7, "8h": 8, "12h": 8, "24h": 0, "48h": 0, "5d": 0, "1w": 0 },
  { avgEntry: "700-800k", "15min": 5, "30min": 6, "1h": 6, "2h": 7, "4h": 7, "6h": 8, "8h": 8, "12h": 0, "24h": 0, "48h": 0, "5d": 0, "1w": 0 },
  { avgEntry: "800-900k", "15min": 6, "30min": 6, "1h": 7, "2h": 7, "4h": 8, "6h": 5, "8h": 9, "12h": 0, "24h": 0, "48h": 0, "5d": 0, "1w": 0 },
  { avgEntry: "900k-1M", "15min": 6, "30min": 0, "1h": 7, "2h": 8, "4h": 8, "6h": 0, "8h": 0, "12h": 0, "24h": 0, "48h": 0, "5d": 0, "1w": 0 },
  { avgEntry: "1-1.5M", "15min": 17, "30min": 75, "1h": 8, "2h": 8, "4h": 9, "6h": 0, "8h": 0, "12h": 0, "24h": 0, "48h": 0, "5d": 0, "1w": 0 },
  { avgEntry: "1.5-2M", "15min": 7, "30min": 8, "1h": 8, "2h": 9, "4h": 0, "6h": 0, "8h": 0, "12h": 0, "24h": 0, "48h": 0, "5d": 0, "1w": 0 },
  { avgEntry: "2-3M", "15min": 8, "30min": 8, "1h": 9, "2h": 0, "4h": 0, "6h": 0, "8h": 0, "12h": 0, "24h": 0, "48h": 0, "5d": 0, "1w": 0 },
  { avgEntry: "3-4M", "15min": 8, "30min": 9, "1h": 0, "2h": 0, "4h": 0, "6h": 0, "8h": 0, "12h": 0, "24h": 0, "48h": 0, "5d": 0, "1w": 0 },
  { avgEntry: "4-5M", "15min": 9, "30min": 0, "1h": 0, "2h": 0, "4h": 0, "6h": 0, "8h": 0, "12h": 0, "24h": 0, "48h": 0, "5d": 0, "1w": 0 }
];

// Helper function to format numbers to thousands with zero decimals
const formatToThousands = (num) => {
  if (!num || isNaN(num)) return 0; // Handle undefined/null/NaN values
  return Math.round(num / 1000) * 1000; // Rounds to the nearest thousand
};

const formatToMillions = (num) => {
  if (!num || isNaN(num)) return 0; // Handle undefined/null/NaN values
  return (num / 1_000_000).toFixed(1); // Convert to millions with 1 decimal
};

// Function to calculate the total value on the chart
const calculateTotalOnChart = (data) => {
  if (!Array.isArray(data) || data.length === 0) return 0; // ✅ Prevents reduce() errors

  return data.reduce((sum, entry) => {
    if (typeof entry !== "object" || entry === null) return sum; // ✅ Skip invalid entries

    Object.keys(entry).forEach((key) => {
      if (key !== "avgEntry") { // ✅ Exclude category labels
        const value = parseFloat(entry[key]) || 0; // ✅ Convert string to number safely
        sum += value;
      }
    });

    return sum;
  }, 0).toFixed(1); // ✅ Round to 1 decimal place
};

// Function to format dataset values
const formatDataNumbers = (data) => {
  return data.map((entry) => {
    const formattedEntry = { ...entry }; // Copy object
    Object.keys(formattedEntry).forEach((key) => {
      if (key !== "avgEntry" && typeof formattedEntry[key] === "number") {
        formattedEntry[key] = formatToMillions(formattedEntry[key]); // Format numbers
      }
    });
    return formattedEntry;
  });
};




const StackChart = ({ input = null, tokenSupply=null }) => {
  
  //When user selects holder we override data
  const data = input && input.length ? formatDataNumbers(input) : defaultData;

  // Calculate total amount on the chart
  const totalOnChart = calculateTotalOnChart(data);
  console.log("totalonchart , suply",totalOnChart+"/"+tokenSupply);
  const relativePercentage = tokenSupply ? ((totalOnChart / tokenSupply*1_000_000) * 100).toFixed(2) : 0;


  console.log("ddd > ",data);
  return (
    <>

    {/* Total on Chart - Displayed Above */}
    <div className="flex justify-left text-sm text-gray-500 mb-2">
        <span className="font-medium mr-10">Total on Chart: {totalOnChart}M</span>
        <span>Relative: {relativePercentage} %</span>
    </div>
    <div
      style={{
        padding: "10px",
        borderRadius: "4px",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "rgba(156, 163, 175, 0.5)",
        transition: "all 0.3s ease",
        backgroundColor: "transparent", // Transparent background
      }}
    >
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
          barSize={10} // Thinner bars
        >
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.2)" />
          <XAxis dataKey="avgEntry" tick={{ fill: "rgba(255, 255, 255, 0.8)" }} />
          <YAxis 
          tick={{ fill: "rgba(255, 255, 255, 0.8)" }} 
          tickFormatter={(value) => `${value}M`} 
          />
          <Tooltip
            cursor={{fill: 'rgba(34, 197, 94, 0.07)'}}
            contentStyle={{
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              borderColor: "#6b7280",
              color: "#ffffff",
            }}
          />
          <Tooltip wrapperStyle={{ width: 100, backgroundColor: '#ccc' }} />
          <Legend />
            {/* Adjusted Bar keys */}
            <Bar dataKey="1w" stackId="a" fill="#8018e7" />
            <Bar dataKey="5d" stackId="a" fill="#6c20a8" />
            <Bar dataKey="48h" stackId="a" fill="#582eb9" />
            <Bar dataKey="24h" stackId="a" fill="#444ac9" />
            <Bar dataKey="12h" stackId="a" fill="#2f68d9" />
            <Bar dataKey="6h" stackId="a" fill="#3b82f6" />
            <Bar dataKey="4h" stackId="a" fill="#4aa1e5" />
            <Bar dataKey="2h" stackId="a" fill="#5bc0d4" />
            <Bar dataKey="1h" stackId="a" fill="#6ddfb3" />
            <Bar dataKey="30m" stackId="a" fill="#82ca9d" />
            <Bar dataKey="15m" stackId="a" fill="#96c284" />
            <Bar dataKey="5m" stackId="a" fill="	#e2ff68" />


        </BarChart>
      </ResponsiveContainer>
    </div>
    </>
  );
};

export default StackChart;
